import React from 'react';
import PropTypes from 'prop-types';

import HelperText from 'Components/UI/Meta/MetaControl/HelperText';
import { usePromptHint } from 'Components/UI/Meta/MetaControl/Types/usePromptHint';
import { getValue } from '../utils';

const defaultMapOptions = v => ({
  name: v.attr('prompt'),
  value: v.id(),
  ...v.attributes(),
});

function Picklist(props) {
  const {
    attrName,
    component,
    disabled,
    onChange,
    onInlineSubmit,
    metaField,
    resource,
    CustomProps: { showReadOnlyOptions, ...OtherCustomProps },
    SpreadProps,
    HelperProps,
    onFocus,
    onBlur,
    ...rest
  } = props;

  const Component = metaField.attr('_component');
  const attribute = resource.attr(attrName);
  const transformValue = metaField.attr('_transformValue') || null;

  const handleChange = (valueOrEvent) => {
    // If given an event, then pull the event.target.value off of it. Otherwise just pass value.
    let value = valueOrEvent?.target
      ? valueOrEvent.target.value
      : valueOrEvent;

    // Allow an override to have a chance to manipulate the value before setting it
    if (transformValue) {
      value = transformValue(value);
    }

    if (onInlineSubmit) {
      onInlineSubmit(value, [attrName, value]);
    } else {
      onChange(value);
      if (onBlur) {
        // call on blur here because the input is closed when an item is selected
        onBlur(value, attrName);
      }
    }
  };

  const dependsKey = metaField.attr('depends');
  const allowEmptyDepends = metaField.attr('_allowEmptyDepends') === true;
  const showAllOptions = metaField.attr('_showAllOptions') === true;
  const dependsValue = dependsKey ? resource.attr(dependsKey) : null;
  const { label } = usePromptHint(metaField);
  const mapOptions = metaField.attr('_mapOptions') || defaultMapOptions;
  const { SelectProps: { multiple } = { multiple: false } } = rest;
  const value = getValue(resource, metaField, attrName,
    {
      formatReadOnly: () => {
        if (resource.attr(attrName)) {
          return resource.selectedOptions(attrName, 'prompt').join(', ');
        }

        return '';
      },
      defaultValue: multiple ? [] : '',
    });

  const options = metaField
    .options()
    .list()
    .filter((v) => {
      if (showAllOptions || !v.attr('depends')) {
        return true;
      }

      return dependsValue && v.attr('depends').includes(dependsValue);
    });

  if (
    metaField.attr('hidden')
    || (metaField.attr('_hiddenWithNoOptions') && options.length === 0)
  ) {
    return null;
  }

  return (
    <Component
      helperText={
        <HelperText
          metaField={metaField}
          {...HelperProps}
        />
      }
      helperTextValue={HelperProps.helperText}
      readonly={metaField.attr('readonly')}
      disabled={
        disabled
        || (!allowEmptyDepends && dependsKey && !dependsValue)
        || (options.length === 0 && metaField.attr('hint') !== 'creatable')
        || (metaField.attr('_disable')
          && metaField.attr('_disable')(resource.attr(attrName)))
      }
      hidden={metaField.attr('hidden')}
      label={label}
      options={
        !showReadOnlyOptions && metaField.attr('readonly')
          ? []
          : options.map(mapOptions)
      }
      required={metaField.attr('required')}
      selectId={`${attribute || attrName}-picklist`}
      value={value}
      onChange={handleChange}
      onClose={onInlineSubmit}
      onFocus={(evt) => onFocus && onFocus(evt, attrName)}
      onBlur={(evt) => onBlur && onBlur(evt, attrName)}
      shouldDisableOption={metaField.attr('_shouldDisableOption')}
      {...SpreadProps}
      {...OtherCustomProps}
      {...rest}
    />
  );
}

Picklist.defaultProps = {
  disabled: false,
  onInlineSubmit: null,
  onFocus: undefined,
  onBlur: undefined,
  HelperProps: {},
};

Picklist.propTypes = {
  HelperProps: PropTypes.object,
  attrName: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  metaField: PropTypes.object.isRequired,
  onInlineSubmit: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  resource: PropTypes.object.isRequired,
};

export default Picklist;
